// ============================================================
// VCS.InterpretAudio -- Mikrofon + Sprachausgabe fuer den Dolmetscher
// mit Partner-Geraet (2026-10-16)
// ============================================================
// Laeuft im Messenger UND auf der Gast-Seite (guest-interpret.php) -- deshalb
// ohne VCS.state und ohne VCS.signaling. Wohin das Audio geht, sagt der
// Aufrufer ueber sendBinary.
//
// Aufnahme wie im Solo-Dolmetscher (live-convo.js): eigener getUserMedia mit
// Echo-Unterdrueckung, 16 kHz PCM16 mono. Wiedergabe: 24 kHz PCM16, die
// Stuecke werden lueckenlos hintereinander geplant.
// ============================================================
window.VCS = window.VCS || {};

VCS.InterpretAudio = class {

    static PCM_SAMPLE_RATE = 16000;
    static TTS_SAMPLE_RATE = 24000;

    constructor(sendBinary) {
        this._sendBinary = sendBinary;
        this._streaming  = false;
        this._micStream  = null;
        this._capCtx     = null;
        this._capSource  = null;
        this._capProc    = null;
        this._capMute    = null;
        this._ttsCtx     = null;
        this._ttsGain    = null;
        this._ttsNextAt  = 0;
    }

    // -- Mikrofon -------------------------------------------------

    get micActive() {
        return !!this._micStream;
    }

    // Senden an/aus, ohne das Mikrofon neu anzufragen. Der Server meldet erst
    // "bereit", wenn sein Erkenner laeuft -- vorher waere Audio verloren.
    setStreaming(on) {
        this._streaming = !!on;
    }

    async startMic() {
        if (this._micStream) {
            return true;
        }
        try {
            this._micStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount    : 1,
                    echoCancellation: true,   // die eigene Sprachausgabe laeuft parallel
                    noiseSuppression: true,
                    autoGainControl : true,
                },
                video: false,
            });
        } catch (err) {
            console.warn('[interpret-audio] Mikrofon-Zugriff fehlgeschlagen: ' + (err?.message || err));
            this._micStream = null;
            return false;
        }

        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) {
            this.stopMic();
            return false;
        }
        const settings = this._micStream.getAudioTracks()[0]?.getSettings?.() || {};
        const streamRate = Number(settings.sampleRate);
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const ctxRate = Number.isFinite(streamRate) && streamRate > 0 ? streamRate : (isSafari ? 48000 : undefined);
        this._capCtx = ctxRate ? new AudioCtx({ sampleRate: ctxRate }) : new AudioCtx();
        if (this._capCtx.state === 'suspended') {
            await this._capCtx.resume().catch(() => {});
        }

        this._capSource = this._capCtx.createMediaStreamSource(this._micStream);
        this._capProc = this._capCtx.createScriptProcessor(4096, 1, 1);
        this._capProc.onaudioprocess = (event) => {
            if (!this._streaming) {
                return;
            }
            if (this._capCtx?.state === 'suspended') {
                // Android 17 / iOS halten den Kontext gern an -- wieder anwerfen.
                this._capCtx.resume().catch(() => {});
                return;
            }
            const input = event.inputBuffer.getChannelData(0);
            const rate = event.inputBuffer.sampleRate || this._capCtx.sampleRate;
            const pcm = this._toInt16(input, rate, VCS.InterpretAudio.PCM_SAMPLE_RATE);
            if (pcm.length) {
                this._sendBinary(pcm.buffer);
            }
        };
        this._capSource.connect(this._capProc);
        // ScriptProcessor braucht einen Ausgang -- stumm, sonst Rueckkopplung.
        this._capMute = this._capCtx.createGain();
        this._capMute.gain.value = 0;
        this._capProc.connect(this._capMute);
        this._capMute.connect(this._capCtx.destination);
        console.info('[interpret-audio] Mikrofon an rate=' + this._capCtx.sampleRate);
        return true;
    }

    stopMic() {
        this._streaming = false;
        if (this._capProc) {
            this._capProc.onaudioprocess = null;
            try { this._capProc.disconnect(); } catch (_) {}
            this._capProc = null;
        }
        if (this._capSource) { try { this._capSource.disconnect(); } catch (_) {} this._capSource = null; }
        if (this._capMute) { try { this._capMute.disconnect(); } catch (_) {} this._capMute = null; }
        if (this._capCtx) { this._capCtx.close().catch(() => {}); this._capCtx = null; }
        if (this._micStream) {
            this._micStream.getTracks().forEach(tr => tr.stop());
            this._micStream = null;
            console.info('[interpret-audio] Mikrofon aus');
        }
    }

    _toInt16(input, inputRate, targetRate) {
        const clamp = v => (v < 0 ? v * 0x8000 : v * 0x7FFF);
        if (!input || !input.length) {
            return new Int16Array(0);
        }
        if (!Number.isFinite(inputRate) || inputRate <= targetRate) {
            const out = new Int16Array(input.length);
            for (let i = 0; i < input.length; i++) out[i] = clamp(Math.max(-1, Math.min(1, input[i])));
            return out;
        }
        const ratio = inputRate / targetRate;
        const outLen = Math.max(1, Math.floor(input.length / ratio));
        const out = new Int16Array(outLen);
        let off = 0;
        for (let o = 0; o < outLen; o++) {
            const next = Math.round((o + 1) * ratio);
            let acc = 0;
            let cnt = 0;
            for (let i = off; i < next && i < input.length; i++) { acc += input[i]; cnt++; }
            out[o] = clamp(Math.max(-1, Math.min(1, cnt ? acc / cnt : 0)));
            off = next;
        }
        return out;
    }

    // -- Sprachausgabe --------------------------------------------

    // Muss aus einer Nutzer-Geste heraus einmal laufen (Beitreten, Mikro,
    // Vorlesen-Schalter) -- sonst bleibt der Ton auf iOS stumm.
    unlock() {
        const ctx = this._ensureTts();
        if (ctx && ctx.state === 'suspended') {
            ctx.resume().catch(() => {});
        }
    }

    _ensureTts() {
        if (this._ttsCtx && this._ttsCtx.state !== 'closed') {
            return this._ttsCtx;
        }
        const Ctor = window.AudioContext || window.webkitAudioContext;
        if (!Ctor) {
            return null;
        }
        let ctx;
        try { ctx = new Ctor({ sampleRate: VCS.InterpretAudio.TTS_SAMPLE_RATE }); }
        catch (_) { ctx = new Ctor(); }
        const gain = ctx.createGain();
        gain.gain.value = 1;
        gain.connect(ctx.destination);
        this._ttsCtx = ctx;
        this._ttsGain = gain;
        this._ttsNextAt = 0;
        return ctx;
    }

    // Ein Stueck abspielen. Rueckgabe: Millisekunden bis alles Eingeplante
    // verklungen ist (fuer den Status "uebersetzt" -> "hoert zu").
    play(b64, sampleRate) {
        const ctx = this._ensureTts();
        if (!ctx) {
            return 0;
        }
        if (ctx.state === 'suspended') {
            ctx.resume().catch(() => {});
        }
        let pcm;
        try {
            pcm = this._decode(b64);
        } catch (err) {
            console.warn('[interpret-audio] Dekodier-Fehler: ' + (err?.message || err));
            return 0;
        }
        if (!pcm.length) {
            return 0;
        }
        const buffer = ctx.createBuffer(1, pcm.length, sampleRate || VCS.InterpretAudio.TTS_SAMPLE_RATE);
        buffer.copyToChannel(pcm, 0, 0);
        const src = ctx.createBufferSource();
        src.buffer = buffer;
        src.connect(this._ttsGain);
        const now = ctx.currentTime;
        let startAt = this._ttsNextAt;
        // Kleiner Vorpuffer nach einer Luecke, sonst knackt der Satzanfang.
        if (startAt < now) {
            startAt = now + 0.12;
        }
        src.start(startAt);
        this._ttsNextAt = startAt + buffer.duration;
        return Math.max(0, (this._ttsNextAt - now) * 1000);
    }

    // Alles Eingeplante sofort verwerfen.
    silence() {
        if (!this._ttsCtx || this._ttsCtx.state === 'closed') {
            return;
        }
        try {
            this._ttsGain.disconnect();
            const gain = this._ttsCtx.createGain();
            gain.gain.value = 1;
            gain.connect(this._ttsCtx.destination);
            this._ttsGain = gain;
        } catch (_) {}
        this._ttsNextAt = 0;
    }

    _decode(b64) {
        const bin = atob(b64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const view = new DataView(bytes.buffer);
        const samples = Math.floor(bytes.length / 2);
        const out = new Float32Array(samples);
        for (let i = 0; i < samples; i++) out[i] = view.getInt16(i * 2, true) / 32768;
        return out;
    }

    release() {
        this.stopMic();
        this._ttsNextAt = 0;
        try { this._ttsGain?.disconnect(); } catch (_) {}
        this._ttsGain = null;
        if (this._ttsCtx) {
            this._ttsCtx.close().catch(() => {});
            this._ttsCtx = null;
        }
    }
};
